/**
 * PDF do COMPROVANTE de pagamento — SERVER-SIDE (como o PDF da cotação).
 *
 * Endpoint PÚBLICO (sem JWT, autorizado pelo token na URL). Busca como **blob**
 * pelo cliente `api`. Duas ações: baixar (download) e visualizar (abre numa aba
 * no mobile / numa modal com iframe no desktop). `?inline=1` para visualizar.
 * Só existe quando o link está pago; senão o backend retorna 409 NOT_PAID.
 */
import api from "../Services/api";

const RECEIPT_PDF_PATH = (token) => `/api/v1/public/payment-link/${token}/comprovante`;

const fetchPdf = (token, { inline = false } = {}) =>
	api.get(RECEIPT_PDF_PATH(token), {
		responseType: "blob",
		params: inline ? { inline: 1 } : undefined,
	});

const filenameFrom = (res, token) => {
	const cd = res.headers?.["content-disposition"] || "";
	const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
	if (match && match[1]) {
		try { return decodeURIComponent(match[1]); } catch (e) { return match[1]; }
	}
	return `comprovante-${String(token).slice(0, 8)}.pdf`;
};

/** Baixa o PDF do comprovante (download). */
export const downloadReceiptPdf = async (token) => {
	if (!token) return;
	const res = await fetchPdf(token);
	const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
	const a = document.createElement("a");
	a.href = url;
	a.download = filenameFrom(res, token);
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(url), 1500);
};

/**
 * Busca o PDF e retorna um **blob URL** p/ exibir num iframe/modal.
 * O chamador é responsável por `URL.revokeObjectURL` ao fechar.
 */
export const fetchReceiptPdfUrl = async (token) => {
	if (!token) return null;
	const res = await fetchPdf(token, { inline: true });
	return URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
};

/** Abre o PDF do comprovante numa nova aba (visualizar — usado no mobile). */
export const viewReceiptPdf = async (token) => {
	if (!token) return;
	// Abre a aba JÁ no clique (evita bloqueio de popup após o await).
	const win = window.open("", "_blank");
	try {
		const res = await fetchPdf(token, { inline: true });
		const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
		if (win && !win.closed) {
			win.location.href = url;
		} else {
			const a = document.createElement("a");
			a.href = url;
			a.download = filenameFrom(res, token);
			document.body.appendChild(a);
			a.click();
			a.remove();
		}
		setTimeout(() => URL.revokeObjectURL(url), 60000);
	} catch (e) {
		if (win && !win.closed) win.close();
		throw e;
	}
};

export default downloadReceiptPdf;
